<template>
  <section
    class="grid grid-cols-1 gap-6 lg:gap-x-14 items-center lg:grid-cols-[380px_1fr]"
  >
    <section
      v-if="slides?.length > 1"
      class="grid grid-cols-1 gap-4 lg:gap-10 order-2 lg:order-0"
    >
      <template v-for="(slide, index) in slides" :key="index">
        <div
          class="grid gap-4 lg:gap-3 auto-rows-content lg:auto-rows-fit transition-[transform, opacity] ease-in-out duration-200 opacity-60 text-surface-500 hover:opacity-90 items-start"
          :class="[
            'lg:grid-cols-[40px_auto]',
            {
              'opacity-100 hover:opacity-100': activeSlideTitle === slide.title,
            },
          ]"
        >
          <p
            class="flex flex-row text-caption items-center gap-x-2"
            :class="[
              'lg:col-start-1',
              {
                'text-mbzuai-sand-500!': activeSlideTitle === slide.title,
              },
            ]"
          >
            0{{ index + 1
            }}<span
              class="border-surface-500 border-b-2 h-px w-[72px] lg:w-4 transition-colors"
              :class="{
                'border-mbzuai-sand-200!': activeSlideTitle === slide.title,
              }"
            ></span>
          </p>

          <h5
            class="hover:cursor-pointer text-mbzuai-sand-500 lg:row-start-1"
            :class="[
              isRtl ? 'lg:col-start-2 text-right' : 'lg:col-start-2 text-left',
              activeSlideTitle === slide.title ? 'text-h5' : 'text-h5-light',
            ]"
            tabindex="0"
            @keydown.space.enter.prevent="gotoSlide(index)"
            @click="gotoSlide(index)"
          >
            {{ slide.title }}
          </h5>

          <MBZCTADefault
            v-show="activeSlideTitle === slide.title"
            :url="{
              title: '',
              href: slide.cta?.buttonUrl?.href!,
            }"
            :label="slide.cta?.buttonLabel ?? 'Read more'"
            :icon="slide.cta?.icon"
            button-style="default"
            class="col-span-full max-w-fit col-start-1 lg:col-start-2 lg:col-span-1 mt-2 lg:mt-0 w-full"
          />
        </div>
      </template>
    </section>

    <section
      class="hero-carousel w-full order-0 m-0 mx-auto overflow-visible"
      :class="isRtl ? 'lg:order-0' : 'lg:order-1'"
    >
      <div
        class="hero-carousel__stage relative w-full overflow-visible aspect-video"
        :class="[
          stageDirectionClass,
          {
            'is-animating': isAnimating,
            'is-playing': isAnimating && playAnimation,
          },
        ]"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
        @touchmove="handleTouchMove"
      >
        <div
          v-if="activeSlide && !isAnimating"
          class="hero-slide hero-slide--active"
        >
          <Responsive
            v-if="activeSlide.cardImage.mediaImageSource?.length"
            :image="activeSlide.cardImage"
            class="hero-slide__image"
          />
          <img
            v-else
            :src="activeSlide.cardImage.mediaImageUrl"
            :alt="activeSlide.cardImage.mediaImageAlt"
            :width="activeSlide.cardImage.mediaImageWidth"
            :height="activeSlide.cardImage.mediaImageHeight"
            fetchpriority="high"
            class="hero-slide__image"
          />
        </div>

        <div
          v-if="outgoingSlide"
          class="hero-slide hero-slide--outgoing opacity-30 z-10"
        >
          <Responsive
            v-if="outgoingSlide.cardImage.mediaImageSource?.length"
            :image="outgoingSlide.cardImage"
            class="hero-slide__image"
          />
          <img
            v-else
            :src="outgoingSlide.cardImage.mediaImageUrl"
            :alt="outgoingSlide.cardImage.mediaImageAlt"
            :width="outgoingSlide.cardImage.mediaImageWidth"
            :height="outgoingSlide.cardImage.mediaImageHeight"
            fetchpriority="high"
            class="hero-slide__image"
          />
        </div>

        <div
          v-if="incomingSlide"
          class="hero-slide hero-slide--incoming z-30"
          @transitionend="onIncomingTransitionEnd"
        >
          <Responsive
            v-if="incomingSlide.cardImage.mediaImageSource?.length"
            :image="incomingSlide.cardImage"
            class="hero-slide__image"
          />
          <img
            v-else
            :src="incomingSlide.cardImage.mediaImageUrl"
            :alt="incomingSlide.cardImage.mediaImageAlt"
            :width="incomingSlide.cardImage.mediaImageWidth"
            :height="incomingSlide.cardImage.mediaImageHeight"
            fetchpriority="high"
            class="hero-slide__image"
          />
        </div>

        <div
          v-if="peekSlide"
          class="hero-slide hero-slide--peek transition-[transform,opacity] opacity-40! z-10 duration-500 ease-in-out"
        >
          <Responsive
            v-if="peekSlide.cardImage.mediaImageSource?.length"
            :image="peekSlide.cardImage"
            class="hero-slide__image"
          />
          <img
            v-else
            :src="peekSlide.cardImage.mediaImageUrl"
            :alt="peekSlide.cardImage.mediaImageAlt"
            :width="peekSlide.cardImage.mediaImageWidth"
            :height="peekSlide.cardImage.mediaImageHeight"
            fetchpriority="high"
            class="hero-slide__image"
          />
        </div>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import type { HeroCarouselSlide } from '~/types';
import Responsive from '../../paragraph/image/Responsive.vue';

const props = defineProps<{
  carousel: HeroCarouselSlide[];
}>();

const { isRtl } = useAppLocale();

const slides = computed(() => {
  const temp: HeroCarouselSlide[] = props.carousel?.map(
    (item: HeroCarouselSlide) => {
      return {
        cardImage: item.cardImage,
        title: item.title,
        cta: item.cta,
      };
    }
  ) as HeroCarouselSlide[];
  return temp;
});

const currentPage = ref<number>(0);
const isAnimating = ref<boolean>(false);
const animationTimer = ref<number | null>(null);
const playAnimation = ref<boolean>(false);
const incomingIndex = ref<number | null>(null);
const outgoingIndex = ref<number | null>(null);
const isSwiping = ref<boolean>(false);
const startX = ref<number>(0);
const startY = ref<number>(0);
const swipeThreshold = ref<number>(50);
const swipeDirection = ref<'next' | 'previous'>('next');

const slideCount = computed(() => slides.value.length);

const getNextIndex = (index: number) => {
  return (index + 1) % slideCount.value;
};

const getPreviousIndex = (index: number) => {
  return (index - 1 + slideCount.value) % slideCount.value;
};

const getLogicalDirectionForTarget = (
  slideIndex: number
): 'next' | 'previous' => {
  if (slideCount.value <= 1) {
    return 'next';
  }

  const forwardDistance =
    (slideIndex - currentPage.value + slideCount.value) % slideCount.value;
  const backwardDistance =
    (currentPage.value - slideIndex + slideCount.value) % slideCount.value;

  return forwardDistance <= backwardDistance ? 'next' : 'previous';
};

const getTargetIndexForDirection = (
  direction: 'next' | 'previous',
  index: number = currentPage.value
) => {
  return direction === 'next' ? getNextIndex(index) : getPreviousIndex(index);
};

// The design uses the same visual entry side for "next" and "previous" inside
// a locale. In LTR both directions enter from the right, and in RTL both
// directions enter from the left.
const getMotionSideForDirection = (
  direction: 'next' | 'previous'
): 'left' | 'right' => {
  if (direction === 'next') {
    return isRtl.value ? 'left' : 'right';
  }

  return isRtl.value ? 'left' : 'right';
};

const stageMotionSide = ref<'left' | 'right'>(
  getMotionSideForDirection('next')
);

const displayDirection = computed(() => {
  return incomingIndex.value !== null || outgoingIndex.value !== null
    ? swipeDirection.value
    : 'next';
});

const stageDirectionClass = computed(() => {
  return stageMotionSide.value === 'left'
    ? 'hero-carousel__stage--from-left'
    : 'hero-carousel__stage--from-right';
});

const finishAnimation = () => {
  if (!isAnimating.value || incomingIndex.value === null) {
    return;
  }

  currentPage.value = incomingIndex.value;
  incomingIndex.value = null;
  outgoingIndex.value = null;
  playAnimation.value = false;
  isAnimating.value = false;
  stageMotionSide.value = getMotionSideForDirection('next');

  if (animationTimer.value !== null) {
    window.clearTimeout(animationTimer.value);
    animationTimer.value = null;
  }
};

const gotoSlide = async (
  slideIndex: number,
  direction: 'next' | 'previous' = getLogicalDirectionForTarget(slideIndex)
) => {
  if (slideIndex === currentPage.value || isAnimating.value) {
    return;
  }

  // Lock the transition side before the incoming/outgoing slides mount so the
  // first rendered frame uses the correct side in both LTR and RTL.
  swipeDirection.value = direction;
  stageMotionSide.value = getMotionSideForDirection(direction);
  outgoingIndex.value = currentPage.value;
  incomingIndex.value = slideIndex;
  isAnimating.value = true;
  playAnimation.value = false;

  await nextTick();
  if (import.meta.client) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        playAnimation.value = true;
      });
    });
  } else {
    playAnimation.value = true;
  }

  if (import.meta.client) {
    animationTimer.value = window.setTimeout(() => finishAnimation(), 500);
  }
};

// Trigger when user touches the screen
const handleTouchStart = (event: TouchEvent) => {
  // get the 1st touch point (single touch only)
  const touch = event.touches[0];
  if (!touch) return;

  startX.value = touch.screenX;
  startY.value = touch.screenY;
  isSwiping.value = true;
};

// triggered when touch moves
const handleTouchMove = (event: TouchEvent) => {
  if (!isSwiping.value) return;

  const touch = event.touches[0];
  if (!touch) return;

  const diffX = touch.screenX - startX.value;
  const diffY = touch.screenY - startY.value;

  if (Math.abs(diffY) > Math.abs(diffX)) {
    isSwiping.value = false;
  }
};

// Triggered when user releases the screen
const handleTouchEnd = (event: TouchEvent) => {
  if (!isSwiping.value) return;

  const touch = event.changedTouches[0];
  if (!touch) return;
  const endX = touch.screenX;
  const deltaX = endX - startX.value;

  // Check if swipe meets threshold
  if (Math.abs(deltaX) > swipeThreshold.value) {
    if (slideCount.value > 1) {
      // Physical swipe direction is mirrored in RTL so that the gesture still
      // follows the visual flow of the carousel.
      const swipeMovesNext = isRtl.value ? deltaX > 0 : deltaX < 0;
      const direction: 'next' | 'previous' = swipeMovesNext
        ? 'next'
        : 'previous';
      const targetIndex = getTargetIndexForDirection(direction);

      gotoSlide(targetIndex, direction);
    }
  }

  isSwiping.value = false;
  startX.value = 0;
  startY.value = 0;
};

const activeSlideTitle = computed(() => {
  return slides.value[currentPage.value]?.title;
});

const hasSlides = computed(() => slides.value?.length > 0);
const activeSlide = computed(() =>
  hasSlides.value ? slides.value[currentPage.value] : null
);

const outgoingSlide = computed(() => {
  if (!hasSlides.value || outgoingIndex.value === null) {
    return null;
  }

  return slides.value[outgoingIndex.value];
});

const incomingSlide = computed(() => {
  if (!hasSlides.value || incomingIndex.value === null) {
    return null;
  }

  return slides.value[incomingIndex.value];
});

const peekSlide = computed(() => {
  if (!hasSlides.value) {
    return null;
  }

  const nextIndex = getNextIndex(currentPage.value);
  const previousIndex = getPreviousIndex(currentPage.value);

  // When idle we preview the next slide. During an active transition we swap
  // the preview to match the requested logical direction.
  return slides.value[
    displayDirection.value === 'previous' ? previousIndex : nextIndex
  ];
});

const onIncomingTransitionEnd = (event: TransitionEvent) => {
  if (event.propertyName !== 'transform') {
    return;
  }

  finishAnimation();
};
</script>

<style scoped>
.hero-carousel {
  max-width: var(--custom-max-width);
}

.hero-slide {
  transition:
    opacity 500ms ease-in-out,
    transform 500ms ease-in-out;
  will-change: transform, opacity;
  position: absolute;
  z-index: 2;
  inset: 0;
  opacity: 1;
}

.hero-slide__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  will-change: auto;
}

.hero-carousel__stage--from-right .hero-slide {
  transform-origin: bottom left;
}

.hero-carousel__stage--from-left .hero-slide {
  transform-origin: bottom right;
}

.hero-carousel__stage--from-right .hero-slide--peek {
  transform: translate3d(103%, 0, 0) scale(0.94);
}

.hero-carousel__stage--from-left .hero-slide--peek {
  transform: translate3d(-103%, 0, 0) scale(0.94);
}

.hero-carousel__stage--from-right .hero-slide--incoming {
  transform: translate3d(100%, 0, 0) scale(0.94);
}

.hero-carousel__stage--from-left .hero-slide--incoming {
  transform: translate3d(-100%, 0, 0) scale(0.94);
}

.hero-slide--outgoing,
.hero-carousel__stage.is-playing .hero-slide--incoming {
  transform: translate3d(0, 0, 0) scale(1);
}

.hero-carousel__stage--from-right.is-playing .hero-slide--outgoing {
  transform: translate3d(103%, 0, 0) scale(0.94);
  opacity: 0.3;
}

.hero-carousel__stage--from-left.is-playing .hero-slide--outgoing {
  transform: translate3d(-103%, 0, 0) scale(0.94);
  opacity: 0.3;
}

.hero-carousel__stage.is-animating .hero-slide--peek {
  opacity: 0;
}

@media (max-width: 1024px) {
  .hero-carousel__stage--from-right .hero-slide--peek {
    transform: translate3d(101%, 0, 0) scale(0.94);
  }

  .hero-carousel__stage--from-left .hero-slide--peek {
    transform: translate3d(-101%, 0, 0) scale(0.94);
  }
}
</style>
